import { Component, Input, HostBinding } from '@angular/core';
import { cn } from '../../../../utils/utils';

export type CustomizableButtonSize = 'sm' | 'md' | 'lg';
export type CustomizableButtonTone = 'default' | 'danger';

/**
 * The console's pill button (the `.bk-btn` look): 40px, Comfortaa 13/700.
 * Filled = ink fill (inverts in dark mode); `outline` = ghost with a
 * hairline border. Layout classes passed in `className` (width, margin,
 * `hidden`, `opacity-50`) still apply; colours, radius and type come from
 * this component so every button reads the same.
 */
@Component({
  selector: 'customizable-button',
  standalone: true,
  imports: [],
  templateUrl: './customizable-button.component.html',
  styleUrl: './customizable-button.component.css',
})
export class CustomizableButtonComponent {
  /** The `title` input would otherwise also land on the host as a native tooltip. */
  @HostBinding('attr.title') readonly hostTitle = null;

  @Input() className: string = '';
  @Input() iconClass: string = '';
  @Input() textClass: string = '';
  @Input() rightIcon: string = '';
  @Input() leftIcon: string = '';
  @Input() outline: boolean = false;
  @Input({ required: true }) title: string = '';
  @Input() loading: boolean = false;
  @Input() disabled: boolean = false;
  @Input() type: 'button' | 'menu' | 'reset' | 'submit' = 'button';
  /**
   * Height: sm 32px, md 40px, lg 48px. When unset it follows the legacy
   * className: `h-[50px]`/`text-lg` reads as lg, `py-1`/`text-xs` as sm.
   */
  @Input() size: CustomizableButtonSize | null = null;
  /**
   * `danger` for destructive actions (red fill, or red text when outlined).
   * When unset, red colour classes in className (bg-red-*, text-red-*,
   * *-danger) imply it.
   */
  @Input() tone: CustomizableButtonTone | null = null;

  merge = cn;

  /**
   * While loading the button stays enabled (aria-disabled) so it keeps
   * keyboard focus; this stops the click from submitting a form or reaching
   * the (click) bound on the host.
   */
  blockWhileLoading(event: Event): void {
    if (!this.loading) return;
    event.preventDefault();
    event.stopPropagation();
  }

  get resolvedSize(): CustomizableButtonSize {
    if (this.size) return this.size;
    const classes = this.className || '';
    if (/h-\[(4[89]|5\d)px\]|(^|\s)!?text-lg(\s|$)/.test(classes)) return 'lg';
    if (/(^|\s)!?(py-0\.5|py-1|text-xs)(\s|$)/.test(classes)) return 'sm';
    return 'md';
  }

  get isDanger(): boolean {
    if (this.tone) return this.tone === 'danger';
    return /(^|[\s:!])(bg|text|border)-(red|danger)(-\d+)?(\/\d+)?(\s|$)/.test(this.className || '');
  }
}
